import { ReactNode, useEffect, useRef, useState } from "react";
import { CoverSheetMainPageContext } from "./context";
import { APP_PATHS, formStates, formTypes, LOG_TYPES } from "../../../constants";
import {
  DigitalLabLogbookInstrumentModelType,
  InstrumentWithSubEquipmentDetailsModelType
} from "../../../models/DigitalLabLogbookInstrumentModelType";
import { useHistory } from "react-router-dom";

const MIN_TABLE_HEIGHT = 500;

const CoverSheetMainPageWrapper = ({ children }: { children: ReactNode }) => {
  const [refreshCoverSheet, setRefreshCoverSheet] = useState(false);
  const [handleOpenChange, setHandleOpenChange] = useState(false);
  const [tabValue, setTabValue] = useState(LOG_TYPES.ADD_RUN_LOG);
  const [openWorkFlowModel, setOpenWorkFlowModel] = useState(false);
  const [logBookEquipment, setLogBookEquipment] = useState<DigitalLabLogbookInstrumentModelType | null>(null);
  const [isClusterForm, setIsClusterForm] = useState(false);
  const [formState, setFormState] = useState(formStates.NEW_FORM);
  const [logType, setLogType] = useState(formTypes.RUN_LOG);
  const [refreshAfterEditOrNew, setRefreshAfterEditOrNew] = useState(false);
  const [allLogType, setAllLogType] = useState(formTypes.RUN_LOG);
  const [allLogHierarchyData, setAllLogHierarchyData] = useState<InstrumentWithSubEquipmentDetailsModelType[] | null>(
    null
  );
  const expandableGroupElement = useRef<HTMLDivElement>(null);
  const expandableLogsElement = useRef<HTMLDivElement>(null);
  const expandableCoverSheetElement = useRef<HTMLDivElement>(null);
  const [isCoverOpen, setIsCoverOpen] = useState(true);
  const [isLogsOpen, setIsLogsOpen] = useState(true);
  const [tableHeight, setTableHeight] = useState(0);
  const isCoverOpenRef = useRef(isCoverOpen);

  useEffect(() => {
    isCoverOpenRef.current = isCoverOpen;
  }, [isCoverOpen]);

  const onCalculateTableHeight = () => {
    const coverSheetOffset = isCoverOpenRef.current ? 350 : 100;
    const expandableGroupOffsetTop = expandableGroupElement.current ? expandableGroupElement.current.offsetTop : 0;
    const otherElementsArbitraryOffset = 350;
    const calculatedHeight = window.innerHeight - expandableGroupOffsetTop - coverSheetOffset - otherElementsArbitraryOffset;

    setTableHeight(Math.max(calculatedHeight, MIN_TABLE_HEIGHT));
  }

  useEffect(() => {
    const handleResize = () => {
      onCalculateTableHeight()
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
}, []);

  useEffect(() => {
    onCalculateTableHeight();
  }, [isCoverOpen, isLogsOpen])

  const history = useHistory();

  const handleOpenWorkFlowModel = ({
    isCluster = false,
    form = formStates.NEW_FORM
  }: {
    isCluster?: boolean | undefined;
    form?: string | undefined;
  }) => {
    setFormState(() => form);
    setIsClusterForm(isCluster);
    setOpenWorkFlowModel(true);
  };

  const handleCloseWorkFlowModel = () => {
    setIsClusterForm(false);
    setOpenWorkFlowModel(false);
    // Stay on the current cover-sheet page when the workflow modal was
    // opened from a cover sheet; otherwise return to the log list (home).
    if (!window.location.pathname.startsWith("/cover-sheet/")) {
      history.push(APP_PATHS.ROOT);
    }
  };

  const loadLogBookEquipment = (logBookEquip: DigitalLabLogbookInstrumentModelType | null) => {
    setLogBookEquipment(() => logBookEquip);
  };

  const loadFormType = (type: string) => {
    setLogType(() => type);
  };

  const loadAllFormType = (type: string) => {
    setAllLogType(() => type);
  };

  const loadAllLogHierarchyData = (type: InstrumentWithSubEquipmentDetailsModelType[] | null) => {
    setAllLogHierarchyData(() => type);
  };

  const loadFormState = (formState: string) => {
    setFormState(() => formState);
  };

  const refreshTable = () => {
    setRefreshAfterEditOrNew((prevState) => !prevState);
  };

  return (
    <CoverSheetMainPageContext.Provider
      value={{
        refreshCoverSheet,
        setRefreshCoverSheet,
        handleOpenChange,
        setHandleOpenChange,
        tabValue,
        setTabValue,
        openWorkFlowModel,
        setOpenWorkFlowModel,
        handleOpenWorkFlowModel,
        handleCloseWorkFlowModel,
        loadLogBookEquipment,
        logBookEquipment,
        isClusterForm,
        formState,
        logType,
        loadFormType,
        loadFormState,
        refreshTable,
        refreshAfterEditOrNew,
        allLogType,
        loadAllFormType,
        allLogHierarchyData,
        loadAllLogHierarchyData,
        expandableGroupElement,
        expandableLogsElement,
        expandableCoverSheetElement,
        isCoverOpen,
        setIsCoverOpen,
        isLogsOpen,
        setIsLogsOpen,
        tableHeight
      }}
    >
      {children}
    </CoverSheetMainPageContext.Provider>
  );
};

export default CoverSheetMainPageWrapper;
